import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtCurrency, csvCell } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Package, RefreshCw, FileDown } from "lucide-react";

const iso = (d) => d.toISOString().slice(0, 10);

export const ReorderReport = () => {
  const qc = useQueryClient();
  const [start, setStart] = useState(iso(new Date(Date.now() - 90 * 864e5)));
  const [end, setEnd] = useState(iso(new Date()));
  const [refreshing, setRefreshing] = useState(false);

  const { data, isFetching } = useQuery({
    queryKey: ["reorder", start, end],
    queryFn: async () => (await api.get("/reorder/report", { params: { start, end } })).data,
  });

  const saveCodes = useMutation({
    mutationFn: async (codes) => (await api.post("/reorder/settings", { included_codes: codes })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["reorder"] }),
    onError: () => toast.error("Could not update code selection"),
  });

  const rows = data?.rows || [];
  const totals = data?.totals || { doses: 0, billed: 0, collected: 0, code_count: 0 };

  const toggle = (code) => {
    const cur = rows.filter((r) => r.included).map((r) => r.code);
    const next = cur.includes(code) ? cur.filter((c) => c !== code) : [...cur, code];
    saveCodes.mutate(next);
  };

  const hardRefresh = async () => {
    setRefreshing(true);
    try {
      await api.get("/reorder/report", { params: { start, end, refresh: 1 } });
      await qc.invalidateQueries({ queryKey: ["reorder"] });
      toast.success("Pulled latest billing from DrChrono");
    } catch {
      toast.error("DrChrono refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  const exportCsv = () => {
    const inc = rows.filter((r) => r.included);
    const head = ["Code", "Item", "Doses", "Billed", "Collected"];
    const body = inc.map((r) => [csvCell(r.code), csvCell(r.name), r.doses, r.billed, r.collected]);
    body.push([]);
    body.push(["TOTAL", `${inc.length} items`, totals.doses, totals.billed, totals.collected]);
    const csv = [head, ...body].map((row) => row.join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `reorder_${start}_to_${end}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm mt-6" data-testid="reorder-report-card">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2.5">
          <span className="h-9 w-9 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
            <Package className="h-[18px] w-[18px]" />
          </span>
          <div>
            <h3 className="font-heading font-bold text-slate-900">Reorder Report</h3>
            <p className="text-xs text-slate-500">Doses &amp; spend per item — live from DrChrono billing</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Input type="date" data-testid="reorder-start" value={start} onChange={(e) => setStart(e.target.value)} className="h-9 w-[150px]" />
          <span className="text-slate-400 text-sm">→</span>
          <Input type="date" data-testid="reorder-end" value={end} onChange={(e) => setEnd(e.target.value)} className="h-9 w-[150px]" />
          <Button variant="outline" size="sm" data-testid="reorder-refresh" onClick={hardRefresh} disabled={refreshing} className="border-slate-300">
            <RefreshCw className={`h-4 w-4 mr-1.5 ${refreshing || isFetching ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button size="sm" data-testid="reorder-export" onClick={exportCsv} disabled={!rows.some((r) => r.included)} className="bg-slate-900 hover:bg-slate-800 text-white">
            <FileDown className="h-4 w-4 mr-1.5" /> Export CSV
          </Button>
        </div>
      </div>

      {data && !data.drchrono_connected && (
        <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3" data-testid="reorder-not-connected">
          Connect DrChrono to pull billing data for the reorder report.
        </div>
      )}

      {data?.drchrono_connected && (
        <>
          <div className="grid grid-cols-3 gap-3 mb-4">
            <Stat label="Items to reorder" value={totals.code_count} />
            <Stat label="Total doses" value={Math.round(totals.doses).toLocaleString()} />
            <Stat label="Billed / Collected" value={`${fmtCurrency(totals.billed)} / ${fmtCurrency(totals.collected)}`} />
          </div>
          <div className="overflow-x-auto max-h-[420px] overflow-y-auto border border-slate-100 rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0 z-10">
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-3 py-2.5 w-10">Use</th>
                  <th className="px-3 py-2.5">Code</th>
                  <th className="px-3 py-2.5">Item</th>
                  <th className="px-3 py-2.5 text-right">Doses</th>
                  <th className="px-3 py-2.5 text-right">Billed</th>
                  <th className="px-3 py-2.5 text-right">Collected</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-400">No billing in this range.</td></tr>
                )}
                {rows.map((r) => (
                  <tr key={r.code} data-testid={`reorder-row-${r.code}`}
                    className={`border-t border-slate-50 ${r.included ? "" : "opacity-45"}`}>
                    <td className="px-3 py-2.5">
                      <Checkbox data-testid={`reorder-toggle-${r.code}`} checked={r.included} onCheckedChange={() => toggle(r.code)} />
                    </td>
                    <td className="px-3 py-2.5 font-mono-metric text-slate-500">{r.code}</td>
                    <td className="px-3 py-2.5 font-medium text-slate-800">{r.name}</td>
                    <td className="px-3 py-2.5 text-right font-mono-metric">{Math.round(r.doses).toLocaleString()}</td>
                    <td className="px-3 py-2.5 text-right font-mono-metric">{fmtCurrency(r.billed)}</td>
                    <td className="px-3 py-2.5 text-right font-mono-metric text-emerald-700">{fmtCurrency(r.collected)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-slate-400 mt-2">
            Tick the items you actually reorder — totals &amp; export include only ticked rows.
            {data.refreshed_at ? ` Last DrChrono pull: ${new Date(data.refreshed_at).toLocaleString()}.` : ""}
          </p>
        </>
      )}
    </div>
  );
};

const Stat = ({ label, value }) => (
  <div className="bg-slate-50 border border-slate-100 rounded-lg p-3">
    <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">{label}</div>
    <div className="mt-1 font-mono-metric text-lg font-bold text-slate-900">{value}</div>
  </div>
);
